import { METIERS_CIBLES } from '../config/servicesCatalog';
import { estOuvertMaintenant } from './horaires';

/**
 * Scoring de prospection — étape 1 (§3.4 du plan).
 *
 * Le barème totalise **100 points** et chaque point est *explicable* : `scoreDetail` est stocké
 * dans le document, ce qui permet à l'agent de dire au téléphone « j'ai vu que votre fiche
 * n'était pas revendiquée » au lieu d'agiter un nombre magique.
 *
 * Fréquences mesurées sur les 50 fiches du run du 29/10/2026 :
 * `google.claimed === false` sur 76 % (35 pts, le signal n°1), note moyenne 4,33, 14,7 avis.
 */

export const BAREME = {
  notClaimed: 35,
  phone: 20,
  address: 15,
  rating: 10,
  reviews: 10,
  trade: 10
};

export const SCORE_DETAIL_VIDE = {
  notClaimed: 0,
  phone: 0,
  address: 0,
  rating: 0,
  reviews: 0,
  trade: 0
};

/**
 * @param {object} prospect document normalisé (ou partiel — la fonction ne lève jamais)
 * @returns {{score: number, scoreDetail: object}}
 */
export const scoreProspect = (prospect = {}) => {
  if (!prospect || typeof prospect !== 'object') {
    return { score: 0, scoreDetail: { ...SCORE_DETAIL_VIDE } };
  }

  // Opposition RGPD : score forcé à 0, le prospect ne sort jamais dans la file.
  if (prospect.optedOut === true) {
    return { score: 0, scoreDetail: { ...SCORE_DETAIL_VIDE } };
  }

  const google = prospect.google || {};
  const adresse = prospect.address || {};

  const detail = {
    notClaimed: google.claimed === false ? BAREME.notClaimed : 0,
    phone: prospect.phoneValid === true ? BAREME.phone : 0,
    address: adresse.isComplete === true ? BAREME.address : 0,
    // Rating absent : 0 point, **pas de pénalité** (2 fiches sans note)
    rating: typeof google.rating === 'number' && google.rating >= 4 ? BAREME.rating : 0,
    reviews: typeof google.reviewCount === 'number' && google.reviewCount >= 5 ? BAREME.reviews : 0,
    trade: METIERS_CIBLES.includes(prospect.targetTrade) ? BAREME.trade : 0
  };

  const score = Object.values(detail).reduce((total, points) => total + points, 0);

  return { score, scoreDetail: detail };
};

/**
 * Statuts de la file d'appel.
 *
 * `qualifie` y figure : sans lui, un prospect « Intéressé » quittait définitivement la file et le
 * `nextCallAt` posé à +48 h n'était jamais relu — aucune relance n'était possible avant l'étape 2.
 */
export const STATUTS_FILE_APPEL = ['a_appeler', 'a_completer', 'rappeler', 'qualifie'];

/**
 * Comparateur de la file : `score` DESC → `address.isComplete` DESC → `reviewCount` DESC.
 *
 * Le `rank` Google n'est **pas** utilisé : il reflète la position dans une recherche locale à un
 * instant donné, pas la propension à acheter. La non-revendication, elle, est structurelle.
 */
export const comparerProspects = (a = {}, b = {}) => {
  const scoreA = Number(a.score) || 0;
  const scoreB = Number(b.score) || 0;
  if (scoreA !== scoreB) return scoreB - scoreA;

  const adresseA = a.address?.isComplete === true ? 1 : 0;
  const adresseB = b.address?.isComplete === true ? 1 : 0;
  if (adresseA !== adresseB) return adresseB - adresseA;

  const avisA = Number(a.google?.reviewCount) || 0;
  const avisB = Number(b.google?.reviewCount) || 0;
  return avisB - avisA;
};

/**
 * File d'appel : filtres durs puis tri.
 * Un prospect fermé **aujourd'hui** est masqué ; un prospect aux horaires inconnus reste
 * visible (12 fiches sur 50 — les masquer reviendrait à les perdre).
 *
 * @param {Array} prospects documents normalisés
 * @param {{statuts?: string[], date?: Date, inclureFermes?: boolean}} options
 */
export const trierFileAppel = (
  prospects = [],
  { statuts = STATUTS_FILE_APPEL, date = new Date(), inclureFermes = false } = {}
) => {
  if (!Array.isArray(prospects)) return [];

  return prospects
    .filter((prospect) => prospect && statuts.includes(prospect.status))
    .filter((prospect) => prospect.optedOut !== true)
    .filter((prospect) => {
      if (inclureFermes) return true;
      const etat = estOuvertMaintenant(prospect.openingHours, date);
      return etat.ouvert !== false;
    })
    .sort(comparerProspects);
};
